import { COPILOT_CHAT_STREAM_IDENTIFIERS, DETAIL_REGEX_GLOBAL, SVG_DOWNLOAD } from './constants';

const appendCellContent = (cell, cellValue, appendDetail) => {
    const str = cellValue == null ? '' : String(cellValue);
    const detailToken = new RegExp(DETAIL_REGEX_GLOBAL.source, 'g');
    let lastIndex = 0;
    let match = detailToken.exec(str);

    if (!match) {
        cell.textContent = str;
        return;
    }

    do {
        if (match.index > lastIndex) {
            cell.appendChild(document.createTextNode(str.slice(lastIndex, match.index)));
        }
        if (appendDetail) {
            appendDetail(cell, match[0]);
        } else {
            cell.appendChild(document.createTextNode(match[0]));
        }
        lastIndex = match.index + match[0].length;
        match = detailToken.exec(str);
    } while (match);

    if (lastIndex < str.length) {
        cell.appendChild(document.createTextNode(str.slice(lastIndex)));
    }
};

// DOM element creation utilities
export const createInfoBox = ({ container, isChart, isTable, id, t }) => {
    container.classList.add('dana-chat-info');

    const header = document.createElement('div');
    header.classList.add('dana-chat-info__header');

    const title = document.createElement('div');
    title.classList.add('dana-chat-info__header__title');
    title.textContent = isChart ? t('label_report') : t('label_table');

    const actionsContainer = document.createElement('div');
    actionsContainer.classList.add('dana-chat-info__header__actions');

    // Create action
    const divAction = document.createElement('div');
    divAction.classList.add('dana-chat-info__header__actions__action');

    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(SVG_DOWNLOAD, 'image/svg+xml');
    const svgElement = svgDocument.documentElement;

    const identifier = isChart
        ? COPILOT_CHAT_STREAM_IDENTIFIERS.chart
        : COPILOT_CHAT_STREAM_IDENTIFIERS.table;
    svgElement.setAttribute(identifier, id);
    svgElement.classList.add('dana-chat-info__header__action-icon');

    divAction.appendChild(svgElement);
    actionsContainer.appendChild(divAction);

    header.appendChild(title);
    header.appendChild(actionsContainer);

    const body = document.createElement('div');
    body.classList.add('dana-chat-info__body');
    if (isChart) body.classList.add('dana-chat-info__body__chart');
    if (isTable) body.classList.add('dana-chat-info__body__table');

    container.appendChild(header);
    container.appendChild(body);

    return body;
};

export const replaceFirstTextToken = (root, token, createNode) => {
    if (!root || !token || typeof createNode !== 'function') return null;

    const iterator = document.createNodeIterator(root, NodeFilter.SHOW_TEXT);
    let node = iterator.nextNode();

    while (node) {
        const value = node.nodeValue;
        const index = value.indexOf(token);
        if (index === -1) {
            node = iterator.nextNode();
            continue;
        }

        const tokenNode = node.splitText(index);
        tokenNode.splitText(token.length);
        const replacement = createNode();
        tokenNode.parentNode.replaceChild(replacement, tokenNode);
        return replacement;
    }

    return null;
};

const createPlaceholder = (attribute, id) => {
    const el = document.createElement('div');
    el.setAttribute(attribute, id);
    return el;
};

export const createChartPlaceholder = (id) => createPlaceholder('data-fm_report', id);

export const createTablePlaceholder = (id) => createPlaceholder('data-fm_table', id);

// Table creation utility
export const createTable = (tableData, appendDetail) => {
    if (!tableData?.length) return null;

    const table = document.createElement('table');
    table.classList.add('dana-chat-info__table');
    Object.assign(table.style, {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto',
    });

    const headers = Object.keys(tableData[0]);

    // Calculate column widths more efficiently
    const columnMaxWidths = headers.reduce((acc, header) => {
        acc[header] =
            Math.max(
                header.length,
                ...tableData.map((row) => row[header]?.toString().length || 0),
            ) * 20;
        return acc;
    }, {});

    // Create header row
    const headerRow = document.createElement('tr');
    headers.forEach((headerText) => {
        const headerCell = document.createElement('th');
        headerCell.classList.add('dana-chat-info__table__header-cell');
        headerCell.textContent = headerText;
        headerCell.style.width = `${columnMaxWidths[headerText]}px`;
        headerRow.appendChild(headerCell);
    });
    table.appendChild(headerRow);

    // Create data rows
    tableData.forEach((rowData) => {
        const row = document.createElement('tr');
        headers.forEach((header) => {
            const cell = document.createElement('td');
            cell.classList.add('dana-chat-info__table__row-cell');
            appendCellContent(cell, rowData[header], appendDetail);
            cell.style.width = `${columnMaxWidths[header]}px`;
            row.appendChild(cell);
        });
        table.appendChild(row);
    });

    return table;
};

// CSV conversion utility
export const convertTableToCSV = (table, removeTags) => {
    if (!table) return '';

    const rows = Array.from(table.querySelectorAll('tr'));
    return rows
        .map((row) => {
            const cols = Array.from(row.querySelectorAll('td,th'));
            return cols.map((col) => removeTags(col.innerHTML || '')).join(',');
        })
        .join('\n');
};
